import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { RolePayload } from '../features/roles/types';
import { RequiredFieldsLegend } from './RequiredFieldsLegend';

const schema = z.object({
  name: z.string().min(1, 'Requerido').max(160)
});

type FormValues = z.infer<typeof schema>;

interface Props {
  initialValues?: RolePayload | null;
  onSubmit: (values: FormValues) => void;
  onCancel?: () => void;
  error?: string;
  onNameChange?: (value: string) => void;
}

export function RolesForm({ initialValues, onSubmit, onCancel, error, onNameChange }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues ?? { name: '' }
  });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    } else {
      reset({ name: '' });
    }
  }, [initialValues, reset]);

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Nombre del rol *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="text"
          placeholder="Ej: Gerencia"
          {...register('name')}
          onChange={(e) => {
            register('name').onChange(e);
            onNameChange?.(e.target.value);
          }}
        />
        {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        {!errors.name && error && <p className="text-sm text-destructive">{error}</p>}
      </div>
      <RequiredFieldsLegend />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Guardar
        </button>
        {onCancel && (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            onClick={onCancel}
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}

